import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from "react";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { calcMacroTargets } from "@/lib/macroSolver";
import { loadPendingMeasures, clearPendingMeasures } from "@/lib/pendingMeasures";

export interface BodyMeasures {
  weight: number;
  height: number;
  age: number;
  sex: "male" | "female";
  goal: "lose" | "maintain" | "gain";
  calories: number;
  mealTimes: string[];
  activityLevel: "sedentary" | "light" | "moderate" | "active" | "very_active";
  wantsTraining: boolean;
  preferences: string[];
  mealPreferences?: Record<string, string[]>;
  macroTargets?: { protein: number; carbs: number; fat: number };
}

function withDerivedMacroTargets(measures: BodyMeasures): BodyMeasures {
  if (!measures.weight || !measures.goal || !measures.calories) return measures;

  const { protein, carbs, fat } = calcMacroTargets(measures.weight, measures.goal, measures.calories);
  return {
    ...measures,
    macroTargets: { protein, carbs, fat },
  };
}

/** Each meal key holds an array of "options". Each option is an array of recipe IDs. */
export interface MealSelection {
  breakfast: string[][];
  morning_snack: string[][];
  lunch: string[][];
  snack: string[][];
  dinner: string[][];
  optional: string[][];
}

/** Migrate legacy format (string[]) to new multi-option format (string[][]) */
export function migrateMealSelection(meals: any): MealSelection {
  if (!meals) return defaultMeals;
  const result: any = {};
  for (const key of ["breakfast", "morning_snack", "lunch", "snack", "dinner", "optional"] as const) {
    const val = meals[key];
    if (!val || val.length === 0) {
      result[key] = [[]];
    } else if (Array.isArray(val[0])) {
      result[key] = val;
    } else {
      result[key] = [val];
    }
  }
  return result as MealSelection;
}

/** Get all unique recipe IDs across all options for a meal */
export function getAllRecipeIds(options: string[][]): string[] {
  return [...new Set(options.flat())];
}

export interface SavedDiet {
  id: string;
  name: string;
  measures: BodyMeasures;
  meals: MealSelection;
  is_current: boolean;
  created_at: string;
  updated_at: string;
  plan?: string;
}

export interface DietPlan {
  measures: BodyMeasures;
  meals: MealSelection;
  createdAt: string;
}

interface DietContextType {
  measures: BodyMeasures | null;
  setMeasures: (m: BodyMeasures) => void;
  meals: MealSelection;
  setMeals: (m: MealSelection) => void;
  dietPlan: DietPlan | null;
  saveDiet: (name?: string, overrideMeals?: MealSelection) => Promise<void>;
  selectedPlan: string | null;
  setSelectedPlan: (p: string) => Promise<void>;
  planLoading: boolean;
  savedDiets: SavedDiet[];
  loadDietHistory: (skipCurrentDietSync?: boolean) => Promise<void>;
  loadDiet: (diet: SavedDiet) => void;
  setCurrentDiet: (dietId: string) => Promise<void>;
  deleteDiet: (dietId: string) => Promise<void>;
  currentDietId: string | null;
  historyLoading: boolean;
  saving: boolean;
  // Credit & abuse prevention
  dietCredits: number;
  subscriptionExpiry: string | null;
  lockedSex: string | null;
  lockedHeight: number | null;
  refreshSubscription: () => Promise<void>;
}

const defaultMeals: MealSelection = {
  breakfast: [[]],
  morning_snack: [[]],
  lunch: [[]],
  snack: [[]],
  dinner: [[]],
  optional: [[]],
};

const DietContext = createContext<DietContextType | null>(null);

export const useDiet = () => {
  const ctx = useContext(DietContext);
  if (!ctx) throw new Error("useDiet must be within DietProvider");
  return ctx;
};

export const DietProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [measures, setMeasures] = useState<BodyMeasures | null>(null);
  const [meals, setMeals] = useState<MealSelection>(defaultMeals);
  const [dietPlan, setDietPlan] = useState<DietPlan | null>(null);
  const [selectedPlan, setSelectedPlanState] = useState<string | null>(null);
  const [planLoading, setPlanLoading] = useState(true);

  const [savedDiets, setSavedDiets] = useState<SavedDiet[]>([]);
  const [currentDietId, setCurrentDietId] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Credit & abuse prevention state
  const [dietCredits, setDietCredits] = useState(0);
  const [subscriptionExpiry, setSubscriptionExpiry] = useState<string | null>(null);
  const [lockedSex, setLockedSex] = useState<string | null>(null);
  const [lockedHeight, setLockedHeight] = useState<number | null>(null);
  const authStateVersionRef = useRef(0);

  const resetDietState = useCallback((pendingMeasures: BodyMeasures | null = null) => {
    setMeasures(pendingMeasures ? withDerivedMacroTargets(pendingMeasures) : null);
    setMeals(defaultMeals);
    setDietPlan(null);
    setSelectedPlanState(null);
    setSavedDiets([]);
    setCurrentDietId(null);
    setDietCredits(0);
    setSubscriptionExpiry(null);
    setLockedSex(null);
    setLockedHeight(null);
  }, []);

  // Reset all state when user changes (login/logout)
  useEffect(() => {
    const authStateVersion = ++authStateVersionRef.current;

    if (!user) {
      // Hydrate measures from anonymous sessionStorage if available
      const pending = loadPendingMeasures();
      resetDietState(pending);
      setPlanLoading(false);
      return;
    }

    resetDietState();
    setPlanLoading(true);

    const fetchInitialData = async () => {
      try {
        // Flush pending anonymous measures into the user's profile (one-time)
        const pending = loadPendingMeasures();
        if (pending) {
          try {
            await apiFetch("api-user-measures", undefined, {
              method: "POST",
              body: pending,
            });
          } catch (err) {
            console.warn("[DietContext] Failed to flush pending measures:", err);
          }
          clearPendingMeasures();
        }

        const [subRes, measuresRes] = await Promise.all([
          apiFetch("api-subscription"),
          apiFetch("api-user-measures"),
        ]);

        if (authStateVersionRef.current !== authStateVersion) return;

        setSelectedPlanState(subRes.plan || "free");
        setDietCredits(subRes.diet_credits || 0);
        setSubscriptionExpiry(subRes.expires_at || null);
        setLockedSex(subRes.locked_sex || null);
        setLockedHeight(subRes.locked_height != null ? Number(subRes.locked_height) : null);

        // Hydrate measures from body_data if no current diet loaded yet
        if (measuresRes.body_data) {
          const bd = measuresRes.body_data;
          const nextMeasures = withDerivedMacroTargets({
            weight: bd.weight, height: bd.height, age: bd.age,
            sex: bd.sex || "male", goal: bd.goal || "lose",
            calories: bd.calories || 0,
            mealTimes: bd.mealTimes || [],
            activityLevel: bd.activityLevel || "moderate",
            wantsTraining: false, preferences: [],
            macroTargets: bd.macroTargets || undefined,
          });

          setMeasures((current) => current ?? nextMeasures);
        }
      } catch {
        if (authStateVersionRef.current !== authStateVersion) return;
        setSelectedPlanState("free");
      } finally {
        if (authStateVersionRef.current === authStateVersion) {
          setPlanLoading(false);
        }
      }
    };
    fetchInitialData();
  }, [resetDietState, user]);

  const refreshSubscription = useCallback(async () => {
    const authStateVersion = authStateVersionRef.current;

    try {
      const res = await apiFetch("api-subscription");
      if (authStateVersionRef.current !== authStateVersion || !user) return;
      setSelectedPlanState(res.plan || "free");
      setDietCredits(res.diet_credits || 0);
      setSubscriptionExpiry(res.expires_at || null);
      setLockedSex(res.locked_sex || null);
      setLockedHeight(res.locked_height != null ? Number(res.locked_height) : null);
    } catch {
      // silent
    }
  }, []);

  const loadDietHistory = useCallback(async (skipCurrentDietSync = false) => {
    if (!user) return;

    const authStateVersion = authStateVersionRef.current;
    setHistoryLoading(true);

    try {
      const res = await apiFetch("api-diets", { action: "list" });
      if (authStateVersionRef.current !== authStateVersion || !user) return;

      const diets: SavedDiet[] = (res.data || []).map((d: any) => ({
        ...d,
        meals: migrateMealSelection(d.meals),
          measures: withDerivedMacroTargets(d.measures),
      }));
      setSavedDiets(diets);

      const current = diets.find((d: SavedDiet) => d.is_current);
      if (current) {
        setCurrentDietId(current.id);
        if (!skipCurrentDietSync) {
          const migratedMeals = current.meals;
            const normalizedMeasures = withDerivedMacroTargets(current.measures);
          console.log("[DietContext] loadDietHistory setting measures from current diet:", JSON.stringify({
              weight: normalizedMeasures?.weight, height: normalizedMeasures?.height,
              calories: normalizedMeasures?.calories, macroTargets: normalizedMeasures?.macroTargets,
          }));
            setMeasures(normalizedMeasures);
          setMeals(migratedMeals);
          setDietPlan({
              measures: normalizedMeasures,
            meals: migratedMeals,
            createdAt: current.created_at,
          });
        }
      }
    } catch (e) {
      console.error("Failed to load diet history:", e);
    } finally {
      if (authStateVersionRef.current === authStateVersion) {
        setHistoryLoading(false);
      }
    }
  }, [user]);

  useEffect(() => {
    if (user) loadDietHistory();
  }, [user, loadDietHistory]);

  const handleSetMeasures = (m: BodyMeasures) => {
    const normalizedMeasures = withDerivedMacroTargets(m);
    console.log("[DietContext] setMeasures called:", JSON.stringify({
      weight: normalizedMeasures.weight,
      height: normalizedMeasures.height,
      calories: normalizedMeasures.calories,
      macroTargets: normalizedMeasures.macroTargets,
    }));
    setMeasures(normalizedMeasures);
  };

  const handleSetMeals = (m: MealSelection) => {
    setMeals(m);
  };

  const saveDiet = async (name?: string, overrideMeals?: MealSelection) => {
    if (!measures) return;
    const normalizedMeasures = withDerivedMacroTargets(measures);
    console.log("[DietContext] saveDiet called with measures:", JSON.stringify({
      weight: normalizedMeasures.weight, height: normalizedMeasures.height, calories: normalizedMeasures.calories,
      macroTargets: normalizedMeasures.macroTargets,
    }));
    setSaving(true);
    const mealsToSave = overrideMeals || meals;
    setMeasures(normalizedMeasures);
    const plan: DietPlan = { measures: normalizedMeasures, meals: mealsToSave, createdAt: new Date().toISOString() };
    setDietPlan(plan);

    if (user) {
      try {
        const res = await apiFetch("api-diets", { action: "save" }, {
          method: "POST",
          body: {
            name: name || `Dieta ${new Date().toLocaleDateString("pt-BR")}`,
              measures: normalizedMeasures,
            meals: mealsToSave,
            is_current: true,
          },
        });
        if (res.data) {
          setCurrentDietId(res.data.id);
          // Refresh subscription data (credits may have changed)
          try {
            const subRes = await apiFetch("api-subscription");
            setDietCredits(subRes.diet_credits || 0);
            setLockedSex(subRes.locked_sex || null);
            setLockedHeight(subRes.locked_height != null ? Number(subRes.locked_height) : null);
          } catch {}
          
          await loadDietHistory(true);
        }
      } catch (e: any) {
        console.error("Failed to save diet to backend:", e);
        throw e;
      }
    }
    setSaving(false);
  };

  const loadDiet = (diet: SavedDiet) => {
    const migratedMeals = migrateMealSelection(diet.meals);
    const normalizedMeasures = withDerivedMacroTargets(diet.measures);
    setMeasures(normalizedMeasures);
    setMeals(migratedMeals);
    setDietPlan({
      measures: normalizedMeasures,
      meals: migratedMeals,
      createdAt: diet.created_at,
    });
  };

  const setCurrentDietFn = async (dietId: string) => {
    try {
      await apiFetch("api-diets", { action: "set-current", id: dietId }, { method: "PUT" });
      setCurrentDietId(dietId);
      const diet = savedDiets.find(d => d.id === dietId);
      if (diet) loadDiet(diet);
      await loadDietHistory();
    } catch (e) {
      console.error("Failed to set current diet:", e);
    }
  };

  const deleteDiet = async (dietId: string) => {
    try {
      await apiFetch("api-diets", { action: "delete", id: dietId }, { method: "DELETE" });
      await loadDietHistory();
    } catch (e) {
      console.error("Failed to delete diet:", e);
    }
  };

  const handleSetPlan = async (p: string) => {
    setSelectedPlanState(p);
    try {
      const res = await apiFetch("api-subscription", undefined, {
        method: "POST",
        body: { plan: p },
      });
      // Update credits/expiry from response
      if (res.diet_credits !== undefined) setDietCredits(res.diet_credits);
      if (res.expires_at !== undefined) setSubscriptionExpiry(res.expires_at);
    } catch (e) {
      console.error("Failed to save plan to backend:", e);
      throw e;
    }
  };

  return (
    <DietContext.Provider value={{
      measures, setMeasures: handleSetMeasures,
      meals, setMeals: handleSetMeals,
      dietPlan, saveDiet,
      selectedPlan, setSelectedPlan: handleSetPlan, planLoading,
      savedDiets, loadDietHistory, loadDiet,
      setCurrentDiet: setCurrentDietFn, deleteDiet,
      currentDietId, historyLoading, saving,
      dietCredits, subscriptionExpiry, lockedSex, lockedHeight, refreshSubscription,
    }}>
      {children}
    </DietContext.Provider>
  );
};
